import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltip } from '@angular/material/tooltip';
import {
  catchError,
  combineLatest,
  filter,
  of,
  shareReplay,
  take,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { FileService, PermissionService } from '@ipg/ngx-frontend-toolset';
import { permissionLabel } from '../../../../../models/permission.model';
import { MediaContext } from '../../../../../models/media-context.model';
import { MediaItem } from '../../../../../models/media.model';
import { AvatarComponent } from '../../../../avatar/avatar.component';
import { MediaPickerDialogService } from '../../../../media-picker-dialog/services/media-picker-dialog.service';
import { BaseDialog } from '../../../../dialog/base-dialog';
import { ConfirmHandler } from '../../../../dialog/dialog.model';

export interface ImageActionDialogResult {
  media: MediaItem;
  duration: number;
}

@Component({
  selector: 'app-image-action-dialog',
  imports: [
    AvatarComponent,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatIcon,
    MatIconButton,
    MatProgressSpinner,
    MatSliderModule,
    MatTooltip,
    ReactiveFormsModule,
  ],
  templateUrl: './image-action-dialog.component.html',
  styleUrl: './image-action-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageActionDialogComponent extends BaseDialog<
  {
    selectedMedia?: MediaItem;
    duration?: number;
    context?: MediaContext;
    onConfirm: ConfirmHandler;
  }
> {
  private readonly _dialogRef =
    inject<MatDialogRef<ImageActionDialogComponent>>(MatDialogRef);
  private readonly _mediaPickerDialog = inject(MediaPickerDialogService);
  private readonly _fileService = inject(FileService);

  /**
   * Choosing the image lists the media library, which needs `media:read`.
   * Without it an existing action keeps its image and its duration can still
   * be changed; the dialog says why the image cannot be.
   */
  protected readonly canReadMedia = inject(PermissionService).hasPermission('media:read');
  protected readonly mediaReadLabel = permissionLabel('media:read');

  protected readonly sliderOptions = {
    min: 5,
    max: 30,
    step: 5,
  };

  protected readonly selectedMedia = signal<MediaItem | null>(
    this.data.selectedMedia ?? null,
  );

  protected readonly imagePreviewUrl = computed(() => {
    const media = this.selectedMedia();

    if (!media?.fileId) {
      return undefined;
    }

    return this._fileService.get(media.fileId).href;
  });

  protected readonly durationControl = new FormControl<number>(
    this.data.duration ?? 5,
    { nonNullable: true },
  );

  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);

  handleOpenMediaPicker() {
    const context = this.data.context;

    this._mediaPickerDialog.open(
      (result) =>
        of(result).pipe(
          filter((r): r is MediaItem => r != null),
          tap((media) => this.selectedMedia.set(media)),
        ),
      {
        type: 'image',
        context,
      },
    );
  }

  handleClearMedia() {
    this.selectedMedia.set(null);
  }

  override handleConfirm(): void {
    const media = this.selectedMedia();

    if (!media) {
      return;
    }

    this.submitted.set(true);

    const result: ImageActionDialogResult = {
      media,
      duration: this.durationControl.value,
    };

    const callback$ = this.data.onConfirm(result);

    const source$ = callback$.pipe(
      shareReplay(1),
      catchError((error) => {
        this.submitted.set(false);
        this.loading.set(false);

        return throwError(() => error);
      }),
    );

    combineLatest([source$, timer(300)])
      .pipe(
        take(1),
        tap(() => this._dialogRef.close(true)),
      )
      .subscribe();
  }
}
